import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface ShareSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ShareSheet({ isOpen, onClose }: ShareSheetProps) {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = 'Join us in celebrating the wedding of Sharoof & Adhabiya \u00B7 June 6, 2026';
  const shareTitle = 'Sharoof & Adhabiya Wedding Invitation';

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url,
        });
        onClose();
      } catch {
        // User cancelled
      }
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const socialLinks = [
    {
      name: 'WhatsApp',
      icon: 'ri-whatsapp-line',
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10',
      href: `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${url}`)}`,
    },
    {
      name: 'Facebook',
      icon: 'ri-facebook-circle-line',
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    },
    {
      name: 'Twitter',
      icon: 'ri-twitter-x-line',
      color: 'text-gray-300',
      bg: 'bg-gray-500/10',
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`,
    },
    {
      name: 'Email',
      icon: 'ri-mail-line',
      color: 'text-rose-300',
      bg: 'bg-rose-500/10',
      href: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(`${shareText}\n\n${url}`)}`,
    },
    {
      name: 'SMS',
      icon: 'ri-message-3-line',
      color: 'text-sky-300',
      bg: 'bg-sky-500/10',
      href: `sms:?body=${encodeURIComponent(`${shareText}\n${url}`)}`,
    },
  ];

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 320 }}
            className="fixed bottom-0 left-0 right-0 z-[201] bg-ink-dark rounded-t-3xl p-6 md:p-8 max-w-md mx-auto"
          >
            {/* Handle bar */}
            <div className="w-12 h-1 bg-pearl/20 rounded-full mx-auto mb-6" />

            <h3 className="font-display text-lg text-cream text-center mb-2">
              Share Invitation
            </h3>
            <p className="font-sans text-xs text-pearl/40 text-center mb-8">
              Invite your loved ones to celebrate with us
            </p>

            {/* Native share button (mobile) */}
            {navigator.share && (
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={handleNativeShare}
                className="w-full mb-6 py-3.5 rounded-xl bg-copper/15 border border-copper/25 text-cream font-sans text-sm tracking-wide flex items-center justify-center gap-2"
              >
                <i className="ri-share-forward-line text-copper w-5 h-5 flex items-center justify-center" />
                <span>Open device share</span>
              </motion.button>
            )}

            {/* Social grid */}
            <div className="grid grid-cols-5 gap-4 mb-8">
              {socialLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-2 group"
                  onClick={onClose}
                >
                  <div
                    className={`w-14 h-14 rounded-2xl ${link.bg} flex items-center justify-center transition-transform group-active:scale-90`}
                  >
                    <i className={`${link.icon} ${link.color} text-xl w-6 h-6 flex items-center justify-center`} />
                  </div>
                  <span className="font-sans text-[10px] text-pearl/50 group-hover:text-pearl/80 transition-colors">
                    {link.name}
                  </span>
                </a>
              ))}
            </div>

            {/* Divider */}
            <div className="flex items-center gap-4 mb-6">
              <span className="flex-1 h-px bg-cream/8" />
              <span className="font-sans text-[10px] uppercase tracking-[2px] text-pearl/30">or</span>
              <span className="flex-1 h-px bg-cream/8" />
            </div>

            {/* Copy link */}
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={handleCopyLink}
              className="w-full py-3.5 rounded-xl bg-cream/5 border border-cream/10 text-cream/80 font-sans text-sm tracking-wide flex items-center justify-center gap-2 transition-colors hover:bg-cream/10"
            >
              <i
                className={`${copied ? 'ri-check-line text-emerald-400' : 'ri-link-m'} w-5 h-5 flex items-center justify-center`}
              />
              <span>{copied ? 'Copied to clipboard!' : 'Copy invitation link'}</span>
            </motion.button>

            {/* Cancel */}
            <button
              onClick={onClose}
              className="w-full mt-4 py-3 text-pearl/40 font-sans text-sm hover:text-pearl/60 transition-colors"
            >
              Cancel
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}